import { Button } from "@nwga/ui/components/button";
import { Field } from "@nwga/ui/components/field";
import { Input } from "@nwga/ui/components/input";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { toast } from "sonner";
import { z } from "zod";
import { AuthCard } from "../components/auth-card.tsx";
import { authClient } from "../lib/auth.ts";

/** Mirrors Better Auth's `minPasswordLength` in `packages/auth/src/server.ts`. */
const MIN_PASSWORD_LENGTH = 12;

export const Route = createFileRoute("/reset-password")({
	validateSearch: z.object({
		token: z.string().optional(),
		/** Better Auth appends this when the token is already known to be bad. */
		error: z.string().optional(),
	}),
	component: ResetPasswordPage,
});

function ResetPasswordPage() {
	const { token, error: linkError } = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [error, setError] = React.useState<string | null>(null);
	const [pending, setPending] = React.useState(false);

	// A missing or already-rejected token is a dead end, so say so and offer the
	// way out rather than rendering a form that cannot succeed.
	if (token === undefined || linkError !== undefined) {
		return (
			<AuthCard
				title="That link has expired"
				description="Reset links last one hour and can only be used once. Request a new one and it will arrive in a moment."
			>
				<Button asChild className="w-full">
					<Link to="/forgot-password">Send a new link</Link>
				</Button>
			</AuthCard>
		);
	}

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(null);

		const data = new FormData(event.currentTarget);
		const password = String(data.get("password"));
		const confirmation = String(data.get("confirmation"));

		if (password.length < MIN_PASSWORD_LENGTH) {
			setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
			return;
		}

		if (password !== confirmation) {
			setError("Those two passwords do not match.");
			return;
		}

		setPending(true);
		const result = await authClient.resetPassword({
			newPassword: password,
			token,
		});
		setPending(false);

		if (result.error !== null && result.error !== undefined) {
			setError(result.error.message ?? "That link is no longer valid.");
			return;
		}

		// Every session was invalidated by the reset, so the cache is stale in a
		// way `invalidate` cannot express: clear it rather than refetch it.
		queryClient.clear();
		toast.success("Password changed", {
			description: "Sign in with your new password.",
		});
		await navigate({ to: "/sign-in" });
	};

	return (
		<AuthCard
			title="Choose a new password"
			description="You'll sign in with it straight away."
		>
			<form
				className="space-y-4"
				onSubmit={(event) => void handleSubmit(event)}
			>
				<Field
					label="New password"
					required
					description={`At least ${MIN_PASSWORD_LENGTH} characters.`}
				>
					{(props) => (
						<Input
							{...props}
							name="password"
							type="password"
							autoComplete="new-password"
							minLength={MIN_PASSWORD_LENGTH}
							required
							autoFocus
						/>
					)}
				</Field>

				<Field
					label="Confirm new password"
					required
					errors={error === null ? undefined : [error]}
				>
					{(props) => (
						<Input
							{...props}
							name="confirmation"
							type="password"
							autoComplete="new-password"
							required
						/>
					)}
				</Field>

				<Button type="submit" className="w-full" disabled={pending}>
					{pending ? "Saving…" : "Change password"}
				</Button>
			</form>
		</AuthCard>
	);
}
